import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { riskApi } from '../../utils/riskApi';
import { formatStampShort } from '../../utils/datetime';
import { Modal, SearchSelect } from '../review/reviewUi';
import { Tip } from './riskUi';
import '../../styles/DocReview.css';
import '../../styles/Risk.css';

const TASK_ORDER = ['generate', 'mitigations', 'similar_products', 'similar_projects', 'check', 'ask'];
const TASK_HINT = {
  generate: 'e.g. Focus on battery, alarms and home use by elderly patients. Skip anything we already have.',
  mitigations: 'For every risk that needs a control and has none, find the requirement that controls it.',
  similar_products: 'What failed in similar devices in the last 5 years — and do we cover it?',
  similar_projects: 'Compare with the other project and list what we may have forgotten.',
  check: 'Inconsistent scores, suspicious mitigations, field signal — no AI needed.',
  ask: 'e.g. Which risks depend on the battery, and are they all controlled?',
};
const NO_AI = new Set(['check', 'similar_projects']);
// The "How it works" guide — one card per task. Keep in step with risk_studio.py and user-manual/RISK.md.
const TASK_HELP = {
  generate: {
    icon: '✦', what: 'Proposes NEW risks that are not already in your risk file.',
    needs: 'Any source that describes the product — a URS, IFU, spec, web page or Product Knowledge.',
    get: 'Hazard, situation, harm, cause, severity and probability — each with the source passage behind it. A risk no source supports is labelled Model knowledge.',
    note: 'Press Find duplicates on the result to check every suggestion against the risks in this panel’s filter — the AI confirms which are the same risk.',
  },
  mitigations: {
    icon: '⛨', what: 'For every risk that needs a control and has none, finds the requirement that controls it.',
    needs: 'An Orcanos filter of your requirements. Without one, only new requirement texts are proposed.',
    get: 'The matching requirement with a match score — press Trace it to link it in Orcanos — or the text of a new requirement.',
  },
  similar_products: {
    icon: '⌕', what: 'Looks at what went wrong in similar devices on the market.',
    needs: 'An openFDA source (an FDA product code) — adverse event reports and recalls from the last 5 years.',
    get: 'Failure themes with report counts, whether your file covers each one, and a suggested risk for each gap. These are other devices’ reports, not facts about yours.',
  },
  similar_projects: {
    icon: '⇄', what: 'Compares your risk file with another project’s, and lists what you may have forgotten.',
    needs: 'The risk panel of another project.',
    get: 'Its risks with no close match in yours (text match under 55%), with its level there and your closest risk. Scores are copied from that project.',
    note: 'Matching needs no AI. If AI is on for your account, it also says whether each risk likely applies to your product.',
  },
  check: {
    icon: '✓', what: 'Reviews your own risk file for things that look wrong.',
    needs: 'Nothing — it reads this panel.',
    get: 'Levels that disagree with the FMEA table, probability lowered with no mitigation, a level that rises after mitigation, complaints on an “unlikely” risk, and risks that need a control and have none.',
  },
  ask: {
    icon: '?', what: 'Answers a question about your risk file in plain English.',
    needs: 'Nothing required — linked sources are used when they help.',
    get: 'A short answer that names the risks and requirements it is based on. Nothing to accept.',
  },
};
const KIND_STYLE = {
  pk: ['PK', '#EAE5F5', '#3D2070'], file: ['FILE', '#E3ECFB', '#1F4E9E'], filter: ['FLT', '#F3F0F8', '#3D3858'],
  panel: ['RSK', '#FBE6E3', '#8A2A0B'], url: ['URL', '#FFF1D6', '#7A4B00'], openfda: ['FDA', '#E2F3E8', '#1D6B3A'],
};
const COVER_STYLE = {
  not: ['Not covered', '#FBE0D5', '#8A2A0B'], partly: ['Partly', '#FFF1D6', '#7A4B00'], covered: ['Covered', '#DDF3E4', '#17603A'],
};

/**
 * Risk Studio (mockup screen 2) — the user's vision: link sources, say what you want, see the
 * result. Results are suggestions with evidence; accepted risks go to the same review-before-
 * create screen as an import, and a mitigation is traced only on "Trace it".
 */
export default function StudioPage({ canManagePanels }) {
  const { setupId } = useParams();
  const navigate = useNavigate();
  const [home, setHome] = useState(null);
  const [task, setTask] = useState('generate');
  const [prompt, setPrompt] = useState('');
  const [run, setRun] = useState(null);
  const [adding, setAdding] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [help, setHelp] = useState(false);
  const poll = useRef(null);

  const load = useCallback(async () => {
    try { setHome(await riskApi.studio(setupId)); } catch (e) { setError(e.message); }
  }, [setupId]);
  useEffect(() => { load(); return () => clearInterval(poll.current); }, [load]);

  const watch = useCallback((runId) => {
    clearInterval(poll.current);
    try { localStorage.setItem(`rsk-studio-run:${setupId}`, runId); } catch (e) { /* storage off — no memory */ }
    const tick = async () => {
      try {
        const r = await riskApi.getRun(runId);
        setRun(r);
        if (r.run.status !== 'running') { clearInterval(poll.current); load(); }
      } catch (e) { clearInterval(poll.current); setError(e.message); }
    };
    tick();
    poll.current = setInterval(tick, 1500);
  }, [load, setupId]);

  // Suggestions are kept with their run — coming back reopens the last one viewed (else the newest).
  const reopened = useRef(false);
  useEffect(() => {
    if (!home || reopened.current) return;
    reopened.current = true;
    let last = null;
    try { last = localStorage.getItem(`rsk-studio-run:${setupId}`); } catch (e) { /* ignore */ }
    if (last === '-') return;              // everything was folded last time — keep it so
    const id = (last && home.runs.some((r) => r.id === last)) ? last : home.runs[0]?.id;
    if (id) watch(id);
  }, [home, setupId, watch]);

  const start = async () => {
    setError('');
    const on = (home.sources || []).filter((s) => s.enabled);
    let findCodes = false;
    if (task === 'similar_products' && !on.some((s) => s.kind === 'openfda')) {
      if (!on.length) { setError('Link a source that describes the product first — or add an openFDA source.'); return; }
      // eslint-disable-next-line no-alert
      if (!window.confirm('No FDA product code is linked.\n\nShall I use your sources to find similar products '
        + '(the FDA product code for your kind of device), then search them and continue?')) return;
      findCodes = true;
    }
    setBusy('run');
    try {
      const sources = on.map((s) => s.id);
      const r = await riskApi.run(setupId, { task, prompt, source_ids: sources, find_codes: findCodes });
      watch(r.run_id);
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };

  const cancel = async () => {
    setBusy('cancel');
    try {
      await riskApi.cancelRun(run.run.id);
      watch(run.run.id);
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };

  const toggle = async (s) => {
    try {
      const r = await riskApi.patchSource(s.id, { enabled: !s.enabled });
      setHome((h) => ({ ...h, sources: h.sources.map((x) => (x.id === s.id ? r.source : x)) }));
    } catch (e) { setError(e.message); }
  };
  const removeSource = async (s) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the source “${s.name}”?`)) return;
    try { await riskApi.deleteSource(s.id); load(); } catch (e) { setError(e.message); }
  };

  const decide = async (row, state) => {
    try {
      const r = await riskApi.decide(run.run.id, row.id, state);
      setRun((x) => ({ ...x, result: { ...x.result, rows: x.result.rows.map((y) => (y.id === row.id ? r.row : y)) } }));
    } catch (e) { setError(e.message); }
  };
  const replaceRow = (row) => setRun((x) => ({ ...x, result: { ...x.result, rows: x.result.rows.map((y) => (y.id === row.id ? row : y)) } }));
  const traceIt = async (row) => {
    try {
      const r = await riskApi.traceIt(run.run.id, row.id);
      setRun((x) => ({ ...x, result: { ...x.result, rows: x.result.rows.map((y) => (y.id === row.id ? r.row : y)) } }));
    } catch (e) { setError(e.message); }
  };
  const findDups = async () => {
    setBusy('dups');
    setError('');
    try {
      const r = await riskApi.findDuplicates(run.run.id);
      if (!r.cancelled) setRun((x) => ({ ...x, result: r.result }));
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const cancelDups = async () => {
    try { await riskApi.cancelDuplicates(run.run.id); } catch (e) { setError(e.message); }
  };
  const fold = () => {
    clearInterval(poll.current);
    setRun(null);
    try { localStorage.setItem(`rsk-studio-run:${setupId}`, '-'); } catch (e) { /* ignore */ }
  };
  const deleteRun = async (r) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the run “${home.tasks[r.task] || r.task}” of ${formatStampShort(r.created_at)}?

`
      + 'Its suggestions are removed. Anything already in review, traced or created in Orcanos stays.')) return;
    setBusy(`del:${r.id}`);
    try {
      await riskApi.deleteRun(r.id);
      if (run?.run?.id === r.id) fold();
      setHome((h) => ({ ...h, runs: h.runs.filter((x) => x.id !== r.id) }));
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const toReview = async () => {
    setBusy('review');
    try {
      const r = await riskApi.toReview(setupId);
      navigate(`/risk/import/${r.batch_id}`, { state: { from: `/risk/${setupId}/studio` } });
    } catch (e) { setError(e.message); setBusy(''); }
  };

  if (!home) {
    return <div className="drv-page rsk-page">{error ? <div className="drv-error">{error}</div> : <div className="drv-muted">Loading Risk Studio…</div>}</div>;
  }
  const onCount = home.sources.filter((s) => s.enabled).length;
  const needsAi = !NO_AI.has(task);
  const running = run?.run?.status === 'running';
  const shownRuns = home.runs.slice(0, 8);
  const result = run?.run?.status === 'done' && (
    <Result run={run} onDecide={decide} onTrace={traceIt} onRow={replaceRow} canManage={canManagePanels}
      onFindDups={findDups} onCancelDups={cancelDups} findingDups={busy === 'dups'}
      onOpenRisk={(key) => navigate(`/risk/${setupId}/r/${String(key).split('-').pop()}`)} />
  );
  const accepted = (run?.result?.rows || []).filter((r) => r.state === 'accepted' && (r.kind === 'risk' || (r.kind === 'theme' && r.suggestion))).length;

  return (
    <div className="drv-page rsk-page">
      <div className="rsk-crumbs">
        <Link to="/risk">Risk</Link><span>/</span>
        <span className="rsk-dim">{home.panel.name}</span><span>/</span><strong>Risk Studio</strong>
      </div>
      {error && <div className="drv-error" role="alert">{error}</div>}

      {help && (
        <Modal title="Risk Studio — how it works" labelledBy="rsk-help-title" onClose={() => setHelp(false)}
          subtitle="Link sources, pick what you want, press Run. Results are suggestions you accept or reject — nothing is written to Orcanos until you say so."
          footer={<button type="button" className="drv-btn drv-btn--primary" onClick={() => setHelp(false)}>Got it</button>}>
          <ol className="rsk-help-steps">
            <li><b>1</b><span><strong>Link sources</strong> on the left — files, Orcanos filters, another project’s risks, web pages, openFDA. Untick one to leave it out.</span></li>
            <li><b>2</b><span><strong>Pick a task</strong> and, if you like, tell it more.</span></li>
            <li><b>3</b><span><strong>Review the result</strong> — Accept or Reject each one, then Review &amp; create.</span></li>
          </ol>
          <div className="rsk-help-grid">
            {TASK_ORDER.map((t) => {
              const h = TASK_HELP[t];
              const noAi = NO_AI.has(t);
              return (
                <div key={t} className={`rsk-help-card${task === t ? ' on' : ''}`}>
                  <div className="rsk-help-head">
                    <span className="rsk-help-tico" aria-hidden="true">{h.icon}</span>
                    <strong>{home.tasks[t]}</strong>
                    <span className={`rsk-help-ai${noAi ? ' no' : ''}`}>{noAi ? 'No AI' : 'AI'}</span>
                  </div>
                  <p className="rsk-help-what">{h.what}</p>
                  <dl className="rsk-help-dl">
                    <dt>Needs</dt><dd>{h.needs}</dd>
                    <dt>You get</dt><dd>{h.get}</dd>
                    {h.note && (<><dt>AI</dt><dd>{h.note}</dd></>)}
                  </dl>
                  <button type="button" className="drv-link rsk-small-link rsk-help-use"
                    onClick={() => { setTask(t); setHelp(false); }}>
                    {task === t ? 'Selected ✓' : 'Use this →'}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="rsk-fine" style={{ margin: 0 }}>
            AI tasks take up to a minute or two, and their cost is recorded. Sources are read as data — never as instructions.
            {canManagePanels && ' To bring in an old FMEA spreadsheet, use Import from file instead.'}
          </p>
        </Modal>
      )}

      <div className="rsk-two">
        {/* ---- sources ---- */}
        <aside className="rsk-src-col" aria-label="Sources">
          <div className="rsk-row-between">
            <div className="rsk-lbl">Sources · {onCount} in use</div>
            {canManagePanels && (
              <select className="drv-select rsk-addsrc" value="" aria-label="Add source"
                onChange={(e) => setAdding(e.target.value)}>
                <option value="">+ Add source</option>
                <option value="file">File (URS, IFU, spec, old FMEA)</option>
                <option value="filter">Orcanos filter</option>
                <option value="panel">Risk panel of another project</option>
                <option value="url">Web page</option>
                <option value="openfda">openFDA — similar devices</option>
                {home.has_product_knowledge && <option value="pk">Product Knowledge</option>}
              </select>
            )}
          </div>
          {home.sources.length === 0 && (
            <div className="drv-empty" style={{ padding: 16 }}>
              No sources yet. Link a URS, a requirements filter, another project’s risks or an FDA product code —
              the Studio works from what you link.
            </div>
          )}
          {home.sources.map((s) => {
            const ks = KIND_STYLE[s.kind] || KIND_STYLE.file;
            return (
              <div key={s.id} className="rsk-src" style={s.enabled ? undefined : { opacity: 0.55 }}>
                <div className="rsk-src-ico" style={{ background: ks[1], color: ks[2] }}>{ks[0]}</div>
                <div className="rsk-src-body">
                  <Tip as="div" className="rsk-src-name" tip={s.name}>{s.name}</Tip>
                  <div className="rsk-fine">{s.kind_label} · {s.meta}{s.read_at ? ` · read ${formatStampShort(s.read_at)}` : ''}</div>
                  <div className="rsk-src-acts">
                    {s.link && <a className="drv-link rsk-small-link" style={{ marginLeft: 0 }} href={s.link} target="_blank" rel="noopener noreferrer">open ↗</a>}
                    {canManagePanels && <button type="button" className="drv-link rsk-small-link" style={{ marginLeft: 0 }} onClick={() => removeSource(s)}>remove</button>}
                  </div>
                </div>
                <input type="checkbox" checked={s.enabled} onChange={() => toggle(s)} aria-label={`Use ${s.name}`}
                  style={{ width: 18, height: 18, accentColor: '#5C35A8', marginTop: 6 }} />
              </div>
            );
          })}
          <div className="rsk-fine">Sources are read as data — never as instructions.</div>
        </aside>

        {/* ---- ask + result ---- */}
        <section className="rsk-studio-main">
          <div className="rsk-card rsk-pad rsk-ask">
            <div className="rsk-row-between">
              <div className="rsk-lbl">What do you want?</div>
              <button type="button" className="help-icon rsk-studio-help" onClick={() => setHelp(true)}
                aria-haspopup="dialog" aria-label="How it works" title="How it works — what each task does">?</button>
            </div>
            <div className="rsk-taskrow" role="tablist" aria-label="Task">
              {TASK_ORDER.map((t) => (
                <button key={t} type="button" role="tab" aria-selected={task === t}
                  className={`rsk-task${task === t ? ' on' : ''}`} onClick={() => setTask(t)}>{home.tasks[t]}</button>
              ))}
              {canManagePanels && <Link className="rsk-task" to={`/risk/${setupId}/import`} state={{ from: `/risk/${setupId}/studio` }}>Import from file →</Link>}
            </div>
            <label className="drv-field">
              <span className="drv-lbl">Tell it more <span>— optional</span></span>
              <textarea className="drv-input rsk-textarea" rows={2} value={prompt} placeholder={TASK_HINT[task]}
                onChange={(e) => setPrompt(e.target.value)} />
            </label>
            <div className="rsk-row-between">
              <span className="rsk-fine">
                {onCount} source{onCount === 1 ? '' : 's'} · {needsAi ? 'uses AI (cost is recorded)' : task === 'similar_projects' ? 'matching needs no AI' : 'no AI needed'} ·
                results go to review — nothing is written to Orcanos yet
              </span>
              <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={start}
                disabled={running || busy === 'run' || (needsAi && !home.ai_enabled)}
                title={needsAi && !home.ai_enabled ? 'AI is not enabled for this account' : undefined}>
                {running ? 'Working…' : 'Run'}
              </button>
            </div>
          </div>

          {running && (
            <div className="rsk-card rsk-pad rsk-row-between">
              <div className="drv-muted">{run.step || 'Working'}…</div>
              <button type="button" className="drv-btn" onClick={cancel} disabled={busy === 'cancel'}
                title="Stop this run — any AI call in progress is cancelled">
                {busy === 'cancel' ? 'Cancelling…' : 'Cancel'}
              </button>
            </div>
          )}
          {run?.run?.status === 'failed' && <div className="drv-error">{run.run.error}</div>}
          {run?.run?.status === 'cancelled' && <div className="drv-note">Run cancelled — nothing was kept.</div>}
          {run?.result?.warnings?.length > 0 && (
            <div className="drv-note">{run.result.warnings.map((w) => <div key={w}>{w}</div>)}</div>
          )}

          {/* A finished run opened from outside the list below (older than its 8 rows) shows here. */}
          {run?.run?.status === 'done' && !shownRuns.some((r) => r.id === run.run.id) && result}

          {shownRuns.length > 0 && (
            <div className="rsk-card rsk-pad">
              <div className="rsk-lbl">Runs</div>
              {shownRuns.map((r) => {
                const open = run?.run?.id === r.id && run.run.status === 'done';
                return (
                  <div key={r.id} className={`rsk-runitem${open ? ' open' : ''}`}>
                    <div className="rsk-runhead">
                      <button type="button" className="rsk-runrow" aria-expanded={open}
                        onClick={() => (open ? fold() : watch(r.id))} title={open ? 'Fold' : 'Unfold — show this run’s result'}>
                        <span className="rsk-runchev" aria-hidden="true">{open ? '▾' : '▸'}</span>
                        <span className="rsk-runtxt">
                          <strong>{home.tasks[r.task] || r.task}</strong>
                          <span className="rsk-fine">{r.prompt ? `“${r.prompt.slice(0, 80)}” · ` : ''}{r.created_by_name} · {formatStampShort(r.created_at)} · {r.status}</span>
                        </span>
                      </button>
                      {r.status !== 'running' && (
                        <button type="button" className="drv-link rsk-small-link rsk-rundel" onClick={() => deleteRun(r)}
                          disabled={busy === `del:${r.id}`} title="Delete this run and its suggestions">
                          {busy === `del:${r.id}` ? 'Deleting…' : 'delete'}
                        </button>
                      )}
                    </div>
                    {open && <div className="rsk-runbody">{result}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {canManagePanels && (
            <div className="rsk-card rsk-pad rsk-createbar">
              <span style={{ flex: 1, fontSize: 13 }}>
                <strong>{accepted} accepted in this run</strong> · accepted risks from every run wait in review until you create them in Orcanos
              </span>
              <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={toReview} disabled={busy === 'review'}>
                Review &amp; create
              </button>
            </div>
          )}
        </section>
      </div>

      {adding && (
        <AddSource kind={adding} setupId={setupId} onClose={() => setAdding('')}
          onAdded={() => { setAdding(''); load(); }} />
      )}
    </div>
  );
}

function Evidence({ list }) {
  if (!list?.length) return <span className="rsk-ev rsk-ev--mk">Model knowledge</span>;
  return list.map((e, i) => (
    e.url
      ? <Tip as="a" key={i} className="rsk-ev" href={e.url} target="_blank" rel="noopener noreferrer"
          heading={e.label} tip={e.quote ? <span className="rsk-tip-long">“{e.quote}”</span> : 'Open the source (new tab)'}>{e.label} ↗</Tip>
      : <Tip key={i} className="rsk-ev" heading={e.quote ? e.label : null} tip={e.quote ? <span className="rsk-tip-long">“{e.quote}”</span> : null}>
          {e.label}{e.quote ? ` · “${e.quote.slice(0, 60)}”` : ''}
        </Tip>
  ));
}

function Decide({ row, onDecide, onOpenRisk }) {
  const { setupId } = useParams();
  if (row.orcanos_key) {
    return (
      <div className="rsk-actions" style={{ justifyContent: 'flex-end' }}>
        <span className="rsk-pill" style={{ background: '#DDF3E4', color: '#17603A' }}>In Orcanos ·{' '}
          <button type="button" className="drv-link" style={{ marginLeft: 0, color: 'inherit', fontWeight: 700 }}
            onClick={() => onOpenRisk(row.orcanos_key)}>{row.orcanos_key}</button>
        </span>
      </div>
    );
  }
  if (row.state === 'staged') {
    const st = row.review_status === 'failed' ? 'Failed in review' : row.review_status === 'skipped' ? 'Skipped in review' : 'In review';
    return (
      <div className="rsk-actions" style={{ justifyContent: 'flex-end' }}>
        {row.staged_batch
          ? <Link className="rsk-pill" style={{ background: '#EAE5F5', color: '#3D2070' }} to={`/risk/import/${row.staged_batch}`} state={{ from: `/risk/${setupId}/studio` }}>{st} →</Link>
          : <span className="rsk-pill" style={{ background: '#EAE5F5', color: '#3D2070' }}>{st}</span>}
      </div>
    );
  }
  return (
    <div className="rsk-actions" style={{ justifyContent: 'flex-end' }}>
      <button type="button" className={`drv-btn${row.state === 'rejected' ? ' drv-btn--primary' : ''}`} onClick={() => onDecide(row, row.state === 'rejected' ? '' : 'rejected')}>Reject</button>
      <button type="button" className={`drv-btn${row.state === 'accepted' ? ' drv-btn--primary' : ''}`} onClick={() => onDecide(row, row.state === 'accepted' ? '' : 'accepted')}>
        {row.state === 'accepted' ? 'Accepted ✓' : 'Accept'}
      </button>
    </div>
  );
}

function Result({ run, onDecide, onTrace, onRow, canManage, onOpenRisk, onFindDups, onCancelDups, findingDups }) {
  const { task } = run.run;
  const res = run.result;
  const rows = res.rows || [];
  const dc = res.dup_check;
  return (
    <div className="rsk-result">
      <div className="rsk-row-between">
        <h2 className="rsk-card-title">{res.summary}</h2>
        <span className="rsk-fine">run {formatStampShort(run.run.finished_at)}</span>
      </div>

      {task === 'generate' && rows.length > 0 && (
        <div className="rsk-card rsk-pad rsk-row-between">
          <span className="rsk-fine">
            {dc
              ? `Checked against ${dc.risks} risks in the risk filter · ${dc.flagged} already there${dc.ai ? '' : ' · wording only (no AI)'} · ${formatStampShort(dc.at)}`
              : 'Are any of these already in your risk file? Checks every suggestion against the risks in this panel’s filter.'}
          </span>
          <button type="button" className="drv-btn" onClick={onFindDups} disabled={findingDups}
            title="Re-read the risk filter and flag suggestions that are already risks in it">
            {findingDups ? 'Checking…' : dc ? 'Check again' : 'Find duplicates'}
          </button>
          {findingDups && (
            <button type="button" className="drv-btn" onClick={onCancelDups}
              title="Stop the check — the AI call in progress is cancelled">Cancel</button>
          )}
        </div>
      )}

      {task === 'ask' && (
        <div className="rsk-card rsk-pad">
          <div className="rsk-fine">“{run.run.prompt}”</div>
          <p className="rsk-ai-text">{res.answer}</p>
          <div className="rsk-actions">{(res.keys || []).map((k) => <span key={k} className="rsk-ev">{k}</span>)}</div>
        </div>
      )}

      {(task === 'generate' || task === 'similar_projects') && rows.map((r) => (
        <article key={r.id} className={`rsk-sug${r.state === 'accepted' || r.orcanos_key ? ' rsk-sug--acc' : ''}${r.state === 'rejected' ? ' rsk-sug--rej' : ''}`}>
          <div className="rsk-row-between" style={{ alignItems: 'flex-start' }}>
            <div>
              <div className="rsk-sug-title">{r.name}</div>
              <div className="rsk-fine">
                {task === 'similar_projects' ? `From ${r.source_key} · level there ${r.level_there || '—'} · closest here ${r.closest || 'none'} (${Math.round((r.closest_score || 0) * 100)}%)` : (r.failure_mode || r.cause || '')}
              </div>
            </div>
            <div className="rsk-actions">
              {(r.severity || r.probability) && <span className="rsk-pill" style={{ background: '#F3F0F8', color: '#3D3858' }}>S{r.severity || '?'} × P{r.probability || '?'}</span>}
              {!r.orcanos_key && r.dup_checked && !r.dups?.length && <span className="rsk-pill" style={{ background: '#DDF3E4', color: '#17603A' }}>New — not in the file</span>}
              {!r.orcanos_key && !r.dup_checked && r.dup_key && <span className="rsk-pill" style={{ background: '#FFF1D6', color: '#7A4B00' }}>Looks like {r.dup_key} · {Math.round(r.dup_score * 100)}%</span>}
            </div>
          </div>
          {!r.orcanos_key && r.dups?.length > 0 && (
            <div className="drv-note" style={{ margin: '6px 0' }}>
              <strong>Already in your risk file:</strong>
              {r.dups.map((d) => (
                <div key={d.key}>
                  <button type="button" className="drv-link" style={{ marginLeft: 0 }} onClick={() => onOpenRisk(d.key)}>{d.key}</button>
                  {' '}{d.name}{d.reason ? <span className="rsk-fine"> — {d.reason}</span> : null}
                </div>
              ))}
            </div>
          )}
          <div className="rsk-kv">
            {r.situation &&<><span>Hazardous situation</span><span>{r.situation}</span></>}
            {r.hazard && <><span>Hazard</span><span>{r.hazard}</span></>}
            {r.harm && <><span>Harm</span><span>{r.harm}</span></>}
            {r.why_scores && <><span>Why these scores</span><span>{r.why_scores}</span></>}
            {r.mitigation_key && <><span>Mitigation</span><span>{r.mitigation_key} — existing requirement, will be traced</span></>}
            {r.applies && <><span>Does it apply?</span><span>{r.applies}</span></>}
          </div>
          <div className="rsk-actions"><span className="rsk-fine" style={{ fontWeight: 600 }}>Evidence</span><Evidence list={r.evidence} /></div>
          {(canManage || r.orcanos_key) && <Decide row={r} onDecide={onDecide} onOpenRisk={onOpenRisk} />}
        </article>
      ))}

      {task === 'mitigations' && (
        <Mitigations runId={run.run.id} rows={rows} canManage={canManage} onTrace={onTrace} onRow={onRow} onOpenRisk={onOpenRisk} />
      )}

      {task === 'similar_products' && (
        <div className="rsk-card">
          <div className="rsk-pad rsk-fine" style={{ borderBottom: '1px solid #EFECF6' }}>
            openFDA — field reports of similar devices, not facts about yours. Counts are as reported to the FDA.
          </div>
          {rows.map((t) => {
            const cs = COVER_STYLE[t.coverage] || COVER_STYLE.not;
            return (
              <div key={t.id} className="rsk-theme">
                <div className="rsk-row-between" style={{ alignItems: 'flex-start' }}>
                  <div style={{ flex: '1 1 320px' }}>
                    <div style={{ fontWeight: 600 }}>{t.name}</div>
                    <div className="rsk-fine">{t.reports.toLocaleString()} reports{t.recalls ? ` · ${t.recalls} recalls` : ''}{t.problems?.length ? ` · ${t.problems.slice(0, 4).join(', ')}` : ''}</div>
                  </div>
                  <div className="rsk-actions">
                    <span className="rsk-pill" style={{ background: cs[1], color: cs[2] }}>{cs[0]}{t.risk_keys?.length ? ` · ${t.risk_keys.join(', ')}` : ''}</span>
                    <Evidence list={t.evidence} />
                  </div>
                </div>
                {t.suggestion && (
                  <div className="rsk-theme-sug">
                    <div><strong>Suggested risk:</strong> {t.suggestion.name} — {t.suggestion.situation} → {t.suggestion.harm}
                      {(t.suggestion.severity || t.suggestion.probability) && ` (S${t.suggestion.severity || '?'} × P${t.suggestion.probability || '?'})`}</div>
                    {(canManage || t.orcanos_key) && <Decide row={t} onDecide={onDecide} onOpenRisk={onOpenRisk} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {task === 'check' && (
        <div className="rsk-card rsk-pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.length === 0 && <div className="rsk-dim">Nothing looks wrong.</div>}
          {rows.map((f) => (
            <div key={f.id} className="rsk-look">
              <span className="rsk-pill" style={{ background: '#FFF1D6', color: '#7A4B00' }}>{f.finding}</span>
              <span className="rsk-look-text"><strong>{f.text}</strong> · {f.count}</span>
              <span className="rsk-look-keys">{f.keys.slice(0, 8).map((k) => (
                <button key={k} type="button" className="drv-link" onClick={() => onOpenRisk(k)}>{k}</button>))}
                {f.count > 8 && <span className="rsk-fine">+{f.count - 8}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddSource({ kind, setupId, onClose, onAdded }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [url, setUrl] = useState('');
  const [types, setTypes] = useState([]);
  const [type, setType] = useState('');
  const [filters, setFilters] = useState({ filters: [], loading: false });
  const [filterId, setFilterId] = useState('');
  const [panels, setPanels] = useState([]);
  const [other, setOther] = useState('');
  const [term, setTerm] = useState('');
  const [codes, setCodes] = useState([]);
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    if (kind === 'filter') riskApi.studioTypes(setupId).then((d) => setTypes(d.types || [])).catch((e) => setError(e.message));
    if (kind === 'panel') riskApi.panels().then((d) => setPanels((d.panels || []).filter((p) => p.id !== setupId))).catch((e) => setError(e.message));
  }, [kind, setupId]);

  useEffect(() => {
    if (kind !== 'filter' || !type) return;
    const t = types.find((x) => x.code === type);
    setFilters({ filters: [], loading: true });
    riskApi.panelConfig(setupId)
      .then((cfg) => riskApi.filters(cfg.project_id, type, t?.label || ''))
      .then((d) => setFilters({ filters: d.filters || [], loading: false }))
      .catch(() => setFilters({ filters: [], loading: false }));
  }, [kind, type, types, setupId]);

  const save = async (body) => {
    setBusy(true);
    setError('');
    try { await (body instanceof File ? riskApi.uploadSource(setupId, body) : riskApi.addSource(setupId, body)); onAdded(); } catch (e) { setError(e.message); setBusy(false); }
  };

  const titles = { file: 'Add a file', filter: 'Add an Orcanos filter', panel: 'Add another project’s risks', url: 'Add a web page', openfda: 'Add openFDA — similar devices', pk: 'Add Product Knowledge' };
  let body = null;
  let action = null;
  if (kind === 'file') {
    body = (
      <label className="rsk-drop">
        <input type="file" accept=".pdf,.docx,.txt,.md,.xlsx,.csv" onChange={(e) => e.target.files?.[0] && save(e.target.files[0])} disabled={busy} />
        <strong>{busy ? 'Reading…' : 'Choose a PDF, Word, Excel, CSV or text file'}</strong>
        <span>A URS, IFU, spec or an old risk file. Only its text is kept.</span>
      </label>
    );
  } else if (kind === 'filter') {
    body = (
      <>
        <label className="drv-field"><span className="drv-lbl">Item type</span>
          <select className="drv-select" value={type} onChange={(e) => { setType(e.target.value); setFilterId(''); }}>
            <option value="">— pick a type —</option>
            {types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </label>
        <label className="drv-field"><span className="drv-lbl">Filter</span>
          <SearchSelect ariaLabel="Filter" disabled={!type || filters.loading} value={filterId} onChange={(v) => setFilterId(String(v))}
            placeholder={filters.loading ? 'Loading filters…' : 'Search filters…'}
            options={(filters.filters || []).map((f) => ({ value: String(f.id), label: `${f.name} (#${f.id})` }))} />
        </label>
        <p className="drv-small" style={{ margin: 0 }}>Requirements here let “Identify mitigations” match existing requirements; complaints and CAPAs give field signal.</p>
      </>
    );
    const t = types.find((x) => x.code === type);
    const f = (filters.filters || []).find((x) => String(x.id) === filterId);
    action = () => save({ kind: 'filter', name: f ? `${t?.label} — ${f.name}` : '', ref: { item_type: type, item_label: t?.label, filter_id: filterId } });
  } else if (kind === 'panel') {
    body = (
      <label className="drv-field"><span className="drv-lbl">Risk panel</span>
        <select className="drv-select" value={other} onChange={(e) => setOther(e.target.value)}>
          <option value="">— pick a panel —</option>
          {panels.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
    );
    action = () => save({ kind: 'panel', ref: { setup_id: other } });
  } else if (kind === 'url') {
    body = (
      <label className="drv-field"><span className="drv-lbl">https:// address</span>
        <input className="drv-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" autoFocus />
      </label>
    );
    action = () => save({ kind: 'url', ref: { url: url.trim() } });
  } else if (kind === 'openfda') {
    body = (
      <>
        <label className="drv-field"><span className="drv-lbl">Find the FDA product code by device name</span>
          <div className="rsk-actions">
            <input className="drv-input" style={{ flex: 1 }} value={term} onChange={(e) => setTerm(e.target.value)} placeholder="e.g. infusion pump" />
            <button type="button" className="drv-btn" onClick={() => riskApi.fdaCodes(term).then((d) => setCodes(d.codes || [])).catch((e) => setError(e.message))}>Search</button>
          </div>
        </label>
        {codes.map((c) => (
          <label key={c.code} className="drv-chk">
            <input type="checkbox" checked={picked.includes(c.code)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, c.code] : p.filter((x) => x !== c.code)))} />
            <span><strong>{c.code}</strong> {c.name} · class {c.class} · {c.specialty}</span>
          </label>
        ))}
        <p className="drv-small" style={{ margin: 0 }}>The wrong code gives a full, convincing, irrelevant list — check it is your kind of device. MAUDE and recalls for the last 5 years are read.</p>
      </>
    );
    action = () => save({ kind: 'openfda', ref: { codes: picked } });
  } else if (kind === 'pk') {
    body = <p className="drv-small" style={{ margin: 0 }}>Links this project’s Product Knowledge — intended use, users, environment and subsystems.</p>;
    action = () => save({ kind: 'pk' });
  }
  return (
    <Modal title={titles[kind]} onClose={onClose}
      footer={action ? (
        <>
          <button type="button" className="drv-btn drv-btn--lg" onClick={onClose}>Cancel</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={action} disabled={busy}>{busy ? 'Reading…' : 'Add source'}</button>
        </>
      ) : null}>
      {body}
      {error && <div className="drv-error" role="alert">{error}</div>}
    </Modal>
  );
}

/** Identify mitigations — an existing requirement is traced ("Trace it"); a proposed NEW one can be
 * created in Orcanos and traced to the risk in one press ("Add requirement"), shown only when the
 * person may add the requirement-filter's type and the risk type (the server enforces it again). */
function Mitigations({ runId, rows, canManage, onTrace, onRow, onOpenRisk }) {
  const [target, setTarget] = useState(undefined);
  const [edit, setEdit] = useState(null);               // { id, name, description }
  const [saving, setSaving] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    riskApi.reqTarget(runId).then((r) => { if (live) setTarget(r.target); }).catch(() => { if (live) setTarget(null); });
    return () => { live = false; };
  }, [runId]);
  const canAdd = !!target?.can_add;
  const open = (r) => {
    const text = r.new_requirement || '';
    const short = text.replace(/^the system shall\s+/i, '').split(/[,.;]/)[0].trim();
    setErr('');
    setEdit({ id: r.id, name: (short.charAt(0).toUpperCase() + short.slice(1)).slice(0, 120), description: text });
  };
  const create = async () => {
    setSaving(edit.id);
    setErr('');
    try {
      const r = await riskApi.createMitigation(runId, edit.id, { name: edit.name, description: edit.description });
      onRow(r.row);
      setEdit(null);
    } catch (e) { setErr(e.message); } finally { setSaving(''); }
  };
  const ok = { background: '#DDF3E4', color: '#17603A' };
  return (
    <div className="rsk-card rsk-table-wrap">
      {target === null && rows.some((r) => !r.key) && (
        <div className="rsk-pad rsk-fine" style={{ borderBottom: '1px solid #EFECF6' }}>Link a requirement filter in Sources to add new requirements to Orcanos from here.</div>
      )}
      {target && !canAdd && (
        <div className="rsk-pad rsk-fine" style={{ borderBottom: '1px solid #EFECF6' }}>{target.why_not || target.error}</div>
      )}
      {canAdd && (
        <div className="rsk-pad rsk-fine" style={{ borderBottom: '1px solid #EFECF6' }}>New requirements are added as <strong>{target.label}</strong> and traced to their risk.</div>
      )}
      <table className="rsk-table" style={{ minWidth: 860 }}>
        <thead><tr><th>Risk</th><th>Existing requirement that controls it</th><th>Match</th><th>Or a new requirement</th><th style={{ textAlign: 'right' }}>Action</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td><button type="button" className="drv-link" onClick={() => onOpenRisk(r.risk_key)}>{r.risk_key}</button><div className="rsk-name">{r.risk_name}</div></td>
              <td className="rsk-sub">{r.key
                ? <>{r.url ? <a href={r.url} target="_blank" rel="noreferrer"><strong>{r.key}</strong></a> : <strong>{r.key}</strong>} {r.requirement}<div className="rsk-fine">{r.why}</div>{r.trace_error && <div className="drv-error" style={{ marginTop: 4 }}>Created, but the trace failed: {r.trace_error}</div>}</>
                : <span className="rsk-dim">No match</span>}</td>
              <td>{r.created ? <span className="rsk-pill" style={ok}>New</span>
                : r.key ? <span className="rsk-pill" style={r.score >= 80 ? ok : { background: '#FFF1D6', color: '#7A4B00' }}>{r.score}%</span> : '—'}</td>
              <td className="rsk-sub">
                {edit?.id === r.id ? (
                  <div style={{ display: 'grid', gap: 6 }}>
                    <label className="rsk-fine">Name<input className="drv-input" value={edit.name} maxLength={255} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></label>
                    <label className="rsk-fine">Description<textarea className="drv-input" rows={4} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></label>
                    {err && <div className="drv-error">{err}</div>}
                  </div>
                ) : (r.new_requirement || '—')}
              </td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {r.key && (r.state === 'traced'
                  ? <span className="rsk-pill" style={ok}>Traced ✓</span>
                  : (canManage || r.created) && <button type="button" className="drv-btn drv-btn--primary" onClick={() => onTrace(r)}>Trace it</button>)}
                {!r.key && r.new_requirement && (edit?.id === r.id ? (
                  <div style={{ display: 'grid', gap: 6, justifyItems: 'end' }}>
                    <button type="button" className="drv-btn drv-btn--primary" disabled={!!saving || !edit.name.trim()} onClick={create}>{saving === r.id ? 'Adding…' : 'Add and trace'}</button>
                    <button type="button" className="drv-link" onClick={() => setEdit(null)}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 6, justifyItems: 'end' }}>
                    {canAdd && <button type="button" className="drv-btn drv-btn--primary" disabled={!!saving} onClick={() => open(r)}>Add requirement</button>}
                    <button type="button" className={canAdd ? 'drv-link' : 'drv-btn'} onClick={() => navigator.clipboard?.writeText(r.new_requirement)}>Copy text</button>
                  </div>
                ))}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="rsk-dim" style={{ textAlign: 'center', padding: 20 }}>Every risk that needs a control has one.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
